import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, formatDate } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { useAuth } from "../context/AuthContext";
import { Navigate, useSearchParams } from "react-router-dom";
import { FloppyDisk, Plus, X, Buildings, CreditCard, Receipt, Gear, Image as ImageIcon, EnvelopeSimple, GoogleLogo, CheckCircle, Link as LinkIcon, Signature } from "@phosphor-icons/react";
import { toast } from "sonner";
import SignaturePad from "../components/app/SignaturePad";

export default function Settings() {
  const { user } = useAuth();
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("azienda");
  const [emailStatus, setEmailStatus] = useState(null);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (settings) setForm(JSON.parse(JSON.stringify(settings)));
  }, [settings]);

  useEffect(() => {
    (async () => {
      try {
        const [es, gs] = await Promise.all([api.get("/email/status"), api.get("/google/status")]);
        setEmailStatus(es.data);
        setGoogleStatus(gs.data);
      } catch {}
    })();
  }, []);

  // Handle Google OAuth callback redirect
  useEffect(() => {
    const g = searchParams.get("google");
    if (g === "ok") {
      toast.success("Google Calendar collegato");
      setTab("integrazioni");
      api.get("/google/status").then(r => setGoogleStatus(r.data));
    } else if (g === "error") {
      toast.error("Errore connessione Google: " + (searchParams.get("msg") || ""));
      setTab("integrazioni");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (user && user.role !== "super_admin") return <Navigate to="/" replace />;
  if (!form) return <div className="p-12 text-sm text-[#64748B]">Caricamento…</div>;

  async function save() {
    setSaving(true);
    try {
      const { app_name, company, termini_pagamento, metodi_pagamento, categorie_spese, iva_default, condizioni_default, note_default, quote_number_format, email_subject_template, email_body_template, reminder_days, stripe_enabled, acconto_percent, owner_signature_data_url, owner_signer_name } = form;
      await api.put("/settings", {
        app_name, company, termini_pagamento, metodi_pagamento, categorie_spese,
        iva_default: Number(iva_default),
        condizioni_default, note_default, quote_number_format,
        email_subject_template, email_body_template,
        reminder_days: (reminder_days || []).map(Number),
        stripe_enabled: !!stripe_enabled,
        acconto_percent: Number(acconto_percent || 30),
        owner_signature_data_url: owner_signature_data_url || null,
        owner_signer_name: owner_signer_name || null,
      });
      await refresh();
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  }

  function updCompany(k, v) { setForm({ ...form, company: { ...form.company, [k]: v } }); }
  function addItem(field, v = "") { setForm({ ...form, [field]: [...form[field], v] }); }
  function updItem(field, idx, v) { setForm({ ...form, [field]: form[field].map((x, i) => i === idx ? v : x) }); }
  function rmItem(field, idx) { setForm({ ...form, [field]: form[field].filter((_, i) => i !== idx) }); }

  function handleLogoFile(file) {
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error("Logo troppo grande (max 1MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => updCompany("logo_url", e.target.result);
    reader.readAsDataURL(file);
  }

  const tabs = [
    { id: "azienda", label: "Dati Azienda", icon: Buildings },
    { id: "pagamenti", label: "Pagamenti", icon: CreditCard },
    { id: "preventivo", label: "Preventivi", icon: Receipt },
    { id: "email", label: "Email", icon: EnvelopeSimple },
    { id: "integrazioni", label: "Integrazioni", icon: LinkIcon },
    { id: "app", label: "Applicazione", icon: Gear },
  ];

  return (
    <div className="p-4 md:p-12 max-w-[1100px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-3">Configurazione</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-none">Impostazioni</h1>
          <p className="text-sm text-[#64748B] mt-3">Default usati nei preventivi e dati aziendali sui documenti</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-settings-btn"
          className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2 self-start"
        >
          <FloppyDisk size={14} weight="bold" /> {saving ? "Salvataggio…" : "Salva Modifiche"}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 sm:gap-2 mb-6 border-b border-[#E2E8F0] overflow-x-auto thin-scroll -mx-4 px-4 sm:mx-0 sm:px-0">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-3 sm:px-4 py-3 text-[10px] sm:text-xs uppercase tracking-[0.15em] font-bold flex items-center gap-1.5 sm:gap-2 transition-all border-b-2 -mb-px flex-shrink-0 whitespace-nowrap ${
              tab === t.id ? "border-[#0033A0] text-[#0033A0]" : "border-transparent text-[#64748B] hover:text-[#0A0A0A]"
            }`}
          >
            <t.icon size={14} weight="bold" /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "azienda" && (
        <div className="space-y-6">
          <div className="swiss-card p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-4">Logo Aziendale</div>
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              <div className="w-40 h-40 border border-[#E2E8F0] bg-white rounded-sm flex items-center justify-center overflow-hidden">
                {form.company.logo_url ? (
                  <img src={form.company.logo_url} alt="Logo" className="w-full h-full object-contain" data-testid="logo-preview" />
                ) : (
                  <ImageIcon size={32} className="text-[#E2E8F0]" />
                )}
              </div>
              <div className="flex-1 space-y-3">
                <p className="text-xs text-[#64748B]">PNG, JPG. Max 1MB. Apparirà sui preventivi e nell'header dell'app.</p>
                <label className="inline-block">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleLogoFile(e.target.files?.[0])}
                    className="hidden"
                    data-testid="logo-upload-input"
                  />
                  <span className="inline-block px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#0A0A0A] rounded-sm hover:bg-[#0A0A0A] hover:text-white transition-all cursor-pointer">
                    Carica Logo
                  </span>
                </label>
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#64748B]">— oppure —</div>
                <input
                  placeholder="URL dell'immagine"
                  value={form.company.logo_url || ""}
                  onChange={(e) => updCompany("logo_url", e.target.value)}
                  className="w-full bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none"
                  data-testid="logo-url-input"
                />
                {form.company.logo_url && (
                  <button onClick={() => updCompany("logo_url", "")} className="text-xs text-[#D32F2F] hover:underline">
                    Rimuovi logo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="swiss-card p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-4">Dati Identificativi</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Ragione Sociale *" full>
                <input value={form.company.ragione_sociale || ""} onChange={(e) => updCompany("ragione_sociale", e.target.value)} className="inp" data-testid="company-name-input" />
              </Field>
              <Field label="Partita IVA"><input value={form.company.p_iva || ""} onChange={(e) => updCompany("p_iva", e.target.value)} className="inp" /></Field>
              <Field label="Codice Fiscale"><input value={form.company.codice_fiscale || ""} onChange={(e) => updCompany("codice_fiscale", e.target.value)} className="inp" /></Field>
              <Field label="Indirizzo" full><input value={form.company.indirizzo || ""} onChange={(e) => updCompany("indirizzo", e.target.value)} className="inp" /></Field>
              <Field label="Città"><input value={form.company.citta || ""} onChange={(e) => updCompany("citta", e.target.value)} className="inp" /></Field>
              <Field label="Provincia"><input value={form.company.provincia || ""} onChange={(e) => updCompany("provincia", e.target.value)} className="inp" /></Field>
              <Field label="CAP"><input value={form.company.cap || ""} onChange={(e) => updCompany("cap", e.target.value)} className="inp" /></Field>
              <Field label="Sito Web"><input value={form.company.sito_web || ""} onChange={(e) => updCompany("sito_web", e.target.value)} className="inp" /></Field>
              <Field label="Email"><input type="email" value={form.company.email || ""} onChange={(e) => updCompany("email", e.target.value)} className="inp" /></Field>
              <Field label="Telefono"><input value={form.company.telefono || ""} onChange={(e) => updCompany("telefono", e.target.value)} className="inp" /></Field>
              <Field label="PEC"><input value={form.company.pec || ""} onChange={(e) => updCompany("pec", e.target.value)} className="inp" /></Field>
              <Field label="Codice SDI"><input value={form.company.sdi || ""} onChange={(e) => updCompany("sdi", e.target.value)} className="inp" /></Field>
              <Field label="IBAN" full><input value={form.company.iban || ""} onChange={(e) => updCompany("iban", e.target.value)} className="inp font-mono" /></Field>
            </div>
          </div>
        </div>
      )}

      {tab === "pagamenti" && (
        <div className="space-y-6">
          {/* Sezione Pagamenti Online (Stripe) */}
          <div className="swiss-card p-6 border-l-4 border-l-[#0033A0]">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#0033A0] font-bold mb-1">Pagamenti Online</div>
                <h3 className="font-display text-xl font-bold tracking-tight">Stripe Checkout</h3>
                <p className="text-xs text-[#64748B] mt-1">Permetti ai clienti di pagare i preventivi <strong>firmati</strong> direttamente da Internet con carta, Apple Pay o Google Pay.</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0" data-testid="stripe-toggle-label">
                <input
                  type="checkbox"
                  checked={!!form.stripe_enabled}
                  onChange={(e) => setForm({ ...form, stripe_enabled: e.target.checked })}
                  className="sr-only peer"
                  data-testid="stripe-enabled-toggle"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-[#0033A0]/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0033A0]"></div>
              </label>
            </div>
            {form.stripe_enabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-[#E2E8F0]">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">% Acconto suggerito</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step="1"
                    value={form.acconto_percent ?? 30}
                    onChange={(e) => setForm({ ...form, acconto_percent: e.target.value })}
                    className="inp font-mono"
                    data-testid="acconto-percent-input"
                  />
                  <p className="text-[10px] text-[#64748B] mt-1.5">Mostrato al cliente come opzione alternativa al saldo totale.</p>
                </div>
                <div className="text-xs bg-[#F4F5F7] rounded-sm p-3 leading-relaxed">
                  <strong className="text-[#0033A0]">Come funziona:</strong> dopo che il cliente firma, vedrà nella pagina pubblica due bottoni — Saldo Totale e Acconto. Il pagamento avviene su Stripe (sicuro). Al completamento, si crea automaticamente un Incasso, lo stato del preventivo passa a <em>pagato</em>, ricevi email su <strong>{form?.company?.email || "info@…"}</strong>.
                </div>
              </div>
            )}
          </div>

          <ListEditor
            label="Termini di Pagamento"
            help="Saranno disponibili come opzioni quando crei un preventivo (es. 'Bonifico 30 gg DF FM')"
            items={form.termini_pagamento}
            onAdd={() => addItem("termini_pagamento")}
            onChange={(i, v) => updItem("termini_pagamento", i, v)}
            onRemove={(i) => rmItem("termini_pagamento", i)}
            testid="termini"
          />
          <ListEditor
            label="Metodi di Pagamento"
            help="Es. Bonifico, Assegno, Carta..."
            items={form.metodi_pagamento}
            onAdd={() => addItem("metodi_pagamento")}
            onChange={(i, v) => updItem("metodi_pagamento", i, v)}
            onRemove={(i) => rmItem("metodi_pagamento", i)}
            testid="metodi"
          />
          <ListEditor
            label="Categorie Spese"
            help="Categorie disponibili quando registri una spesa (Carburante, Trasferta, Tasse, ecc.)"
            items={form.categorie_spese || []}
            onAdd={() => addItem("categorie_spese")}
            onChange={(i, v) => updItem("categorie_spese", i, v)}
            onRemove={(i) => rmItem("categorie_spese", i)}
            testid="categorie"
          />
        </div>
      )}

      {tab === "preventivo" && (
        <div className="space-y-6">
          <div className="swiss-card p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-4">Default Preventivo</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="IVA Predefinita (%)">
                <input type="number" min={0} max={100} step="0.5" value={form.iva_default} onChange={(e) => setForm({ ...form, iva_default: e.target.value })} className="inp font-mono" data-testid="iva-default-input" />
              </Field>
              <Field label="Formato Numerazione">
                <input value={form.quote_number_format} onChange={(e) => setForm({ ...form, quote_number_format: e.target.value })} className="inp font-mono" data-testid="number-format-input" />
                <span className="text-[10px] text-[#64748B] mt-1 block">Variabili: {"{YYYY}"}, {"{YY}"}, {"{NNNN}"}, {"{NNN}"}</span>
              </Field>
            </div>
            <div className="mt-4 space-y-4">
              <Field label="Condizioni Generali (testo precompilato)">
                <textarea rows={4} value={form.condizioni_default} onChange={(e) => setForm({ ...form, condizioni_default: e.target.value })} className="inp" data-testid="condizioni-default-input" />
              </Field>
              <Field label="Note Predefinite (opzionale)">
                <textarea rows={3} value={form.note_default} onChange={(e) => setForm({ ...form, note_default: e.target.value })} className="inp" />
              </Field>
            </div>
          </div>
        </div>
      )}

      {tab === "email" && (
        <div className="space-y-6">
          <div className="swiss-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B]">Stato Servizio Email</div>
                <h3 className="font-display text-xl font-bold mt-1">Resend</h3>
              </div>
              {emailStatus?.enabled ? (
                <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold bg-green-100 text-green-800 border border-green-300 rounded-sm flex items-center gap-1.5"><CheckCircle size={12} weight="fill" /> Attivo</span>
              ) : (
                <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold bg-gray-100 text-gray-800 border border-gray-300 rounded-sm">Non configurato</span>
              )}
            </div>
            {emailStatus?.enabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Mittente</div>
                  <div className="font-mono mt-1">{emailStatus.sender_name} &lt;{emailStatus.sender_email}&gt;</div>
                </div>
              </div>
            )}
            <p className="text-xs text-[#64748B] mt-4">
              Mittente di default: <code className="bg-[#F4F5F7] px-1.5 py-0.5 rounded-sm">onboarding@resend.dev</code>.
              Per inviare da un dominio personalizzato (es. <strong>@sagigrp.it</strong>), verifica il dominio su resend.com → Domains e aggiorna SENDER_EMAIL in backend/.env.
            </p>
          </div>

          <div className="swiss-card p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B]">Template Email Preventivo</div>
              <button
                type="button"
                onClick={() => {
                  if (!window.confirm("Ripristinare il testo consigliato? Il testo attuale verrà sovrascritto (ricorda di salvare).")) return;
                  setForm({
                    ...form,
                    email_subject_template: "Preventivo {numero} da {azienda}",
                    email_body_template:
                      "Gentile {cliente},\n\n" +
                      "in allegato troverà il preventivo {numero} per i servizi richiesti, per un totale di {totale} (importo finale netto da pagare, già comprensivo di IVA, Rivalsa cassa previdenziale e al netto della Ritenuta d'acconto dove applicabile).\n\n" +
                      "Può visualizzarlo, firmarlo digitalmente e procedere al pagamento online (carta o bonifico) direttamente dal seguente link:\n{link}\n\n" +
                      "Restiamo a Sua disposizione per qualsiasi chiarimento.\n\n" +
                      "Cordiali saluti,\n{azienda}"
                  });
                  toast.success("Testo consigliato caricato. Clicca Salva Modifiche per applicare.");
                }}
                data-testid="restore-email-template-btn"
                className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#0033A0] hover:underline shrink-0"
              >
                ↺ Ripristina testo consigliato
              </button>
            </div>
            <Field label="Oggetto Email">
              <input value={form.email_subject_template || ""} onChange={(e) => setForm({ ...form, email_subject_template: e.target.value })} className="inp" data-testid="email-subject-input" />
              <span className="text-[10px] text-[#64748B] mt-1 block">Variabili: {"{numero}"}, {"{azienda}"}</span>
            </Field>
            <div className="h-4" />
            <Field label="Corpo Email (predefinito)">
              <textarea rows={9} value={form.email_body_template || ""} onChange={(e) => setForm({ ...form, email_body_template: e.target.value })} className="inp font-mono text-xs" data-testid="email-body-input" />
              <div className="text-[10px] text-[#64748B] mt-2 leading-relaxed">
                <div className="font-bold uppercase tracking-[0.15em] mb-1">Variabili disponibili</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                  <span><code className="bg-[#F4F5F7] px-1 rounded-sm">{"{cliente}"}</code> nome cliente</span>
                  <span><code className="bg-[#F4F5F7] px-1 rounded-sm">{"{numero}"}</code> numero preventivo</span>
                  <span><code className="bg-[#F4F5F7] px-1 rounded-sm">{"{azienda}"}</code> ragione sociale tua</span>
                  <span><code className="bg-[#F4F5F7] px-1 rounded-sm">{"{link}"}</code> link firma/pagamento</span>
                  <span><code className="bg-[#F4F5F7] px-1 rounded-sm">{"{totale}"}</code> totale netto da pagare</span>
                  <span><code className="bg-[#F4F5F7] px-1 rounded-sm">{"{subtotale}"}</code> imponibile servizi</span>
                  <span><code className="bg-[#F4F5F7] px-1 rounded-sm">{"{imponibile}"}</code> imponibile IVA</span>
                  <span><code className="bg-[#F4F5F7] px-1 rounded-sm">{"{iva}"}</code> importo IVA</span>
                  <span><code className="bg-[#F4F5F7] px-1 rounded-sm">{"{iva_perc}"}</code> aliquota IVA %</span>
                  <span><code className="bg-[#F4F5F7] px-1 rounded-sm">{"{rivalsa}"}</code> importo rivalsa</span>
                  <span><code className="bg-[#F4F5F7] px-1 rounded-sm">{"{rivalsa_perc}"}</code> % rivalsa</span>
                  <span><code className="bg-[#F4F5F7] px-1 rounded-sm">{"{ritenuta}"}</code> importo ritenuta</span>
                  <span><code className="bg-[#F4F5F7] px-1 rounded-sm">{"{ritenuta_perc}"}</code> % ritenuta</span>
                </div>
                <div className="mt-2">Doppio a-capo (Invio + Invio) per paragrafo nuovo. Il <strong>riepilogo fiscale dettagliato</strong> (imponibile + IVA + rivalsa + ritenuta + totale) viene aggiunto automaticamente sotto al testo, quindi non serve includerlo manualmente.</div>
              </div>
            </Field>
          </div>

          <div className="swiss-card p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-4">Promemoria Automatici</div>
            <p className="text-xs text-[#64748B] mb-4">Numero di giorni dopo l'invio in cui mostrare il bottone "Promemoria" sul preventivo (puoi inviarlo manualmente).</p>
            <Field label="Giorni (separati da virgola)">
              <input value={(form.reminder_days || []).join(", ")} onChange={(e) => setForm({ ...form, reminder_days: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} placeholder="7, 14" className="inp font-mono" data-testid="reminder-days-input" />
            </Field>
          </div>
        </div>
      )}

      {tab === "integrazioni" && (
        <div className="space-y-6">
          <div className="swiss-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <GoogleLogo size={28} weight="bold" className="text-[#0033A0]" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B]">Sincronizzazione</div>
                  <h3 className="font-display text-xl font-bold mt-0.5">Google Calendar</h3>
                </div>
              </div>
              {googleStatus?.connected ? (
                <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold bg-green-100 text-green-800 border border-green-300 rounded-sm flex items-center gap-1.5"><CheckCircle size={12} weight="fill" /> Connesso</span>
              ) : (
                <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold bg-gray-100 text-gray-800 border border-gray-300 rounded-sm">Non Connesso</span>
              )}
            </div>

            {googleStatus?.connected ? (
              <>
                <div className="bg-[#F4F5F7] border border-[#E2E8F0] rounded-sm p-4 text-sm space-y-2">
                  <div><strong>Account:</strong> {googleStatus.google_email}</div>
                  {googleStatus.connected_at && <div className="text-xs text-[#64748B]">Connesso il {formatDate(googleStatus.connected_at)}</div>}
                </div>
                <p className="text-xs text-[#64748B] mt-4 mb-4">Gli eventi creati/modificati/eliminati nel modulo <strong>Calendario</strong> vengono sincronizzati automaticamente con questo account Google.</p>
                <button
                  onClick={async () => {
                    if (!window.confirm("Vuoi davvero scollegare Google Calendar? Gli eventi futuri non saranno più sincronizzati.")) return;
                    try {
                      await api.post("/google/disconnect");
                      toast.success("Google Calendar scollegato");
                      const r = await api.get("/google/status"); setGoogleStatus(r.data);
                    } catch { toast.error("Errore"); }
                  }}
                  data-testid="google-disconnect-btn"
                  className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#D32F2F] text-[#D32F2F] rounded-sm hover:bg-[#D32F2F] hover:text-white transition-all"
                >
                  Scollega Account
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-[#64748B] mb-4">
                  Collega il tuo account Google per esportare automaticamente gli eventi del Calendario su Google Calendar.
                </p>
                {!googleStatus?.enabled ? (
                  <div className="text-sm text-[#E65C00] bg-[#E65C00]/5 border border-[#E65C00]/20 p-3 rounded-sm">
                    Integrazione non configurata. Imposta <code className="bg-white px-1 rounded-sm">GOOGLE_CLIENT_ID</code> e <code className="bg-white px-1 rounded-sm">GOOGLE_CLIENT_SECRET</code> in backend/.env.
                  </div>
                ) : (
                  <button
                    onClick={async () => {
                      try {
                        const { data } = await api.get("/google/oauth/url");
                        window.location.href = data.url;
                      } catch (e) { toast.error("Errore"); }
                    }}
                    data-testid="google-connect-btn"
                    className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2"
                  >
                    <GoogleLogo size={16} weight="bold" /> Connetti Google Calendar
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {tab === "app" && (
        <div className="space-y-6">
          <div className="swiss-card p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-4">Identità Applicazione</div>
            <Field label="Nome Applicazione">
              <input value={form.app_name} onChange={(e) => setForm({ ...form, app_name: e.target.value })} className="inp" data-testid="app-name-input" />
            </Field>
            <p className="text-xs text-[#64748B] mt-3">Questo nome appare nell'header, nella pagina di accesso e nei documenti pubblici.</p>
          </div>

          <div className="swiss-card p-6" data-testid="owner-signature-section">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#0033A0] mb-1">Controfirma</div>
                <h3 className="font-display text-xl font-bold flex items-center gap-2"><Signature size={20} weight="duotone" /> La tua firma sui Documenti</h3>
                <p className="text-xs text-[#64748B] mt-2">Registra qui la tua firma una volta sola: verrà stampata automaticamente accanto a quella del cliente su ogni Documento firmato (contratti, NDA, lettere d'incarico).</p>
              </div>
            </div>

            <Field label="Nome e Cognome del firmatario aziendale">
              <input
                value={form.owner_signer_name || ""}
                onChange={(e) => setForm({ ...form, owner_signer_name: e.target.value })}
                placeholder="Es. Sara Galati"
                data-testid="owner-signer-name-input"
                className="inp"
              />
            </Field>

            <div className="mt-4">
              <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">Firma</label>
              {form.owner_signature_data_url ? (
                <div className="border border-[#E2E8F0] rounded-sm p-4 bg-white" data-testid="owner-signature-preview">
                  <img
                    src={form.owner_signature_data_url}
                    alt="Firma titolare"
                    className="max-h-32 mx-auto"
                    style={{ filter: "contrast(1.1)" }}
                  />
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E2E8F0]">
                    <span className="text-xs text-[#008A3C] font-bold flex items-center gap-1.5">
                      <CheckCircle size={14} weight="fill" /> Firma registrata
                    </span>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, owner_signature_data_url: null })}
                      data-testid="owner-signature-clear"
                      className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#D32F2F] hover:underline"
                    >
                      Sostituisci firma
                    </button>
                  </div>
                </div>
              ) : (
                <div data-testid="owner-signature-pad-wrapper">
                  <SignaturePad
                    onChange={(dataUrl) => setForm({ ...form, owner_signature_data_url: dataUrl })}
                    height={180}
                  />
                  <p className="text-[11px] text-[#64748B] mt-2">Dopo aver firmato, clicca <strong>Salva Modifiche</strong> in alto a destra per registrare la firma.</p>
                </div>
              )}
            </div>
          </div>

          <BackupExportCard />

          <div className="swiss-card p-6" data-testid="pwa-install-section">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#0033A0] mb-1">Versione Desktop</div>
                <h3 className="font-display text-xl font-bold">Installa su PC / Mac</h3>
                <p className="text-xs text-[#64748B] mt-2">Aggiungi Gestionale 360 al desktop o al menu Start come app dedicata. Apri il gestionale con un click, senza barra del browser e senza distrazioni. Richiede internet (i dati restano sul cloud).</p>
              </div>
              <div className="shrink-0 w-14 h-14 bg-[#0033A0] text-white flex items-center justify-center rounded-sm font-black text-lg">SG</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
              <div className="border border-[#E2E8F0] rounded-sm p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0033A0] mb-2">Chrome · Edge · Brave</div>
                <ol className="text-xs text-[#0A0A0A] space-y-1.5 list-decimal pl-4 leading-relaxed">
                  <li>Apri l'app in <strong>Chrome</strong> o <strong>Edge</strong>.</li>
                  <li>Clicca l'icona <strong>Installa app</strong> nella barra indirizzo (a destra dell'URL).</li>
                  <li>In alternativa: menu <strong>⋮</strong> → <em>Installa Gestionale 360…</em></li>
                  <li>L'app appare nel <strong>Desktop / menu Start</strong> con la sua icona.</li>
                </ol>
              </div>

              <div className="border border-[#E2E8F0] rounded-sm p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0033A0] mb-2">Safari (Mac · iPad · iPhone)</div>
                <ol className="text-xs text-[#0A0A0A] space-y-1.5 list-decimal pl-4 leading-relaxed">
                  <li>Apri l'app in <strong>Safari</strong>.</li>
                  <li>Menu <strong>File</strong> → <em>Aggiungi a Dock</em> (Mac) oppure pulsante <strong>Condividi</strong> → <em>Aggiungi a Home</em> (iOS).</li>
                  <li>L'app si aprirà come finestra dedicata.</li>
                </ol>
              </div>
            </div>

            <div className="mt-4 p-3 bg-[#F4F5F7] border-l-2 border-[#0033A0] text-xs text-[#0A0A0A]">
              <strong>Quando l'app è installata</strong>, perderai la barra del browser e l'icona apparirà sul desktop. Per disinstallare: click destro sull'icona → <em>Disinstalla</em>, oppure apri l'app e fai click sui tre puntini in alto → <em>Disinstalla Gestionale 360</em>.
            </div>
          </div>
        </div>
      )}

      <style>{`
        .inp { width: 100%; background: #fff; border: 1px solid #E2E8F0; border-radius: 4px; padding: 8px 12px; font-size: 14px; outline: none; transition: all 0.15s; }
        .inp:focus { border-color: #0033A0; box-shadow: 0 0 0 1px #0033A0; }
      `}</style>
    </div>
  );
}

function BackupExportCard() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [busy, setBusy] = useState(false);

  async function download(withRange) {
    if (busy) return;
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (withRange) {
        if (dateFrom) params.set("date_from", dateFrom);
        if (dateTo) params.set("date_to", dateTo);
      }
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/export/excel${params.toString() ? "?" + params.toString() : ""}`;
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) {
        const txt = await resp.text();
        toast.error(`Errore export: ${resp.status} ${txt.slice(0, 120)}`);
        return;
      }
      const blob = await resp.blob();
      const cd = resp.headers.get("Content-Disposition") || "";
      const filenameMatch = cd.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `Gestionale360_Export.xlsx`;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Export scaricato con successo");
    } catch (e) {
      toast.error(`Errore export: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="swiss-card p-6 border-l-4 border-l-[#008A3C]" data-testid="backup-export-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#008A3C] font-bold mb-1">Backup e Export</div>
          <h3 className="font-display text-xl font-bold">Scarica tutti i dati in Excel</h3>
          <p className="text-xs text-[#64748B] mt-2 leading-relaxed">Un unico file .xlsx con un foglio per ogni sezione: Clienti · Preventivi · Voci · Incassi · Spese · Catalogo · Abbonamenti · Allegato A · Documenti · Email inviate. Perfetto per commercialista, backup periodico o analisi in Excel.</p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => download(false)}
        disabled={busy}
        data-testid="export-all-btn"
        className="w-full btn-primary px-4 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center justify-center gap-2 mb-4"
      >
        <FloppyDisk size={14} weight="bold" /> {busy ? "Preparazione in corso…" : "Scarica TUTTO in Excel"}
      </button>

      <div className="border-t border-[#E2E8F0] pt-4">
        <div className="text-[10px] uppercase tracking-[0.15em] font-bold mb-2">Oppure: solo un intervallo di date</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div>
            <label className="block text-[10px] text-[#64748B] mb-1">Dal</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="export-date-from" className="inp text-sm" />
          </div>
          <div>
            <label className="block text-[10px] text-[#64748B] mb-1">Al</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="export-date-to" className="inp text-sm" />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => download(true)}
              disabled={busy || (!dateFrom && !dateTo)}
              data-testid="export-range-btn"
              className="w-full px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border-2 border-[#0033A0] text-[#0033A0] rounded-sm hover:bg-[#0033A0] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Scarica Range
            </button>
          </div>
        </div>
        <p className="text-[10px] text-[#64748B] mt-2">Se compili solo la data di inizio o di fine, il filtro sarà aperto sull'estremo mancante.</p>
      </div>
    </div>
  );
}



function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function ListEditor({ label, help, items, onAdd, onChange, onRemove, testid }) {
  return (
    <div className="swiss-card p-6">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B]">{label}</div>
        <button onClick={onAdd} data-testid={`add-${testid}`} className="text-xs uppercase tracking-[0.15em] font-bold text-[#0033A0] hover:underline flex items-center gap-1">
          <Plus size={12} weight="bold" /> Aggiungi
        </button>
      </div>
      <p className="text-xs text-[#64748B] mb-4">{help}</p>
      {items.length === 0 ? (
        <div className="text-sm text-[#64748B] py-6 text-center border border-dashed border-[#E2E8F0] rounded-sm">
          Nessuna voce. Aggiungi la prima.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                value={it}
                onChange={(e) => onChange(i, e.target.value)}
                className="flex-1 bg-white border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none"
                data-testid={`${testid}-item-${i}`}
              />
              <button onClick={() => onRemove(i)} className="p-2 text-[#64748B] hover:text-[#D32F2F]" data-testid={`remove-${testid}-${i}`}>
                <X size={16} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
